import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/hooks/use-alert';

export const useDocumentActions = (projectId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(['pages.projectDetails']);
  const { confirm } = useAlert();
  const [documentActionLoading, setDocumentActionLoading] = useState<string | null>(null);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareExpiresIn, setShareExpiresIn] = useState<number | undefined>(undefined);
  const [shareDocumentName, setShareDocumentName] = useState<string | undefined>(undefined);

  // Upload document mutation
  const uploadDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('gewerk', 'Allgemein');

      // Use multipart upload to new endpoint
      const response = await apiRequest(`/api/projects/${projectId}/documents`, {
        method: 'POST',
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/project", projectId] });
      setShowDocumentUpload(false);
      setUploadFile(null);
      setIsUploadingDocument(false);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      setIsUploadingDocument(false);
    }
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Include projectId as query parameter when deleting from project context
      const url = projectId 
        ? `/api/documents/${documentId}?projectId=${encodeURIComponent(projectId)}`
        : `/api/documents/${documentId}`;
      const response = await apiRequest(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/project", projectId] });
    }
  });

  const handleViewDocument = async (document: any) => {
    setDocumentActionLoading(`view-${document.id}`);
    
    // Open window immediately to avoid popup blocker (must be synchronous with user action)
    const newWindow = window.open('about:blank', '_blank');
    
    try {
      const response = await apiRequest(`/api/documents/${document.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      
      // Parse JSON response containing the file URL
      const data = await response.json();
      
      if (!data.fileUrl) {
        throw new Error('No file URL returned');
      }
      
      // Navigate the already-opened window to the file URL directly
      if (newWindow) {
        newWindow.location.href = data.fileUrl;
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      // Close the blank window if there was an error
      if (newWindow) {
        newWindow.close();
      }
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const handleDownloadDocument = async (document: any) => {
    setDocumentActionLoading(`download-${document.id}`);
    try {
      const response = await apiRequest(`/api/documents/${document.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.fileName || document.name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading document:', error);
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const result = await confirm({
      title: t('documents.deleteConfirmTitle'),
      message: t('documents.deleteConfirmMessage'),
      variant: 'destructive'
    });
    if (result) {
      deleteDocumentMutation.mutate(documentId);
    }
  };

  const handleUploadClick = () => {
    setShowDocumentUpload(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setUploadFile(file);
  };

  const handleDocumentUpload = () => {
    if (!uploadFile) return;
    
    setIsUploadingDocument(true);
    uploadDocumentMutation.mutate(uploadFile);
  };

  const handleUploadCancel = () => {
    setShowDocumentUpload(false);
    setUploadFile(null);
    setIsUploadingDocument(false);
  };

  const handleShareDocument = async (document: any) => {
    setDocumentActionLoading(`share-${document.id}`);
    try {
      const response = await apiRequest(`/api/documents/${document.id}/token`);
      if (!response.ok) {
        throw new Error('Failed to generate share token');
      }

      const data = await response.json();
      const token = data.data?.token || data.token;
      const expiresIn = data.data?.expiresIn || data.expiresIn || 3600; // Default 1 hour
      // Use canonical URL from backend when present (signed share flow)
      const shareableUrl =
        data.data?.url ||
        `${window.location.origin}/api/documents/${document.id}/view?token=${encodeURIComponent(token ?? '')}`;

      setShareUrl(shareableUrl);
      setShareExpiresIn(Math.floor(expiresIn / 3600)); // Convert seconds to hours
      setShareDocumentName(document.name || document.fileName);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Error generating share link:', error);
      toast({
        title: t('toasts.error'),
        description: t('documents.shareError'),
        variant: 'destructive',
      });
    } finally {
      setDocumentActionLoading(null);
    }
  };

  return {
    // State
    documentActionLoading,
    showDocumentUpload,
    uploadFile,
    isUploadingDocument,
    shareDialogOpen,
    setShareDialogOpen,
    shareUrl,
    shareExpiresIn,
    shareDocumentName,
    
    // Actions
    handleViewDocument,
    handleDownloadDocument,
    handleDeleteDocument,
    handleShareDocument,
    handleUploadClick,
    handleFileSelect,
    handleDocumentUpload,
    handleUploadCancel,
    
    // Mutations
    uploadDocumentMutation,
    deleteDocumentMutation
  };
};
